import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { giftCardsApi } from '../api/giftCards'
import Boton from '../components/Boton'
import MoneyInput from '../components/MoneyInput'
import ModalListaClientes from '../components/ModalListaClientes'
import { descargarArchivo } from '../utils/descargar'
import { toISOLocal } from '../utils/fecha'
import { colors, text, sx } from '../design/tokens'

const inputSx = {
  width: '100%', padding: '8px 10px', border: `1px solid ${colors.textPlh}`,
  borderRadius: 6, fontSize: text.body, boxSizing: 'border-box',
}
const lblSx = {
  display: 'block', fontSize: text.meta, fontWeight: 600, color: colors.textSec,
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
}

function venceDefault() {
  const d = new Date()
  d.setFullYear(d.getFullYear() + 1)
  return toISOLocal(d)
}

const fmt = (n) => (Number(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * Parsea un monto en formato argentino ("20.000" / "20.000,50" / "20000").
 * Con coma: los puntos son miles y la coma decimal. Sin coma: los puntos son
 * miles (criterio AR: el decimal se escribe con coma).
 */
function parseMonto(s) {
  const clean = String(s ?? '').trim().replace(/[^\d.,]/g, '')
  if (!clean) return NaN
  const n = clean.includes(',')
    ? clean.replace(/\./g, '').replace(',', '.')
    : clean.replace(/\./g, '')
  return parseFloat(n)
}

/**
 * Parsea el pegado de Excel del modo lista. Una fila por tarjeta, columnas
 * separadas por tab o ";": Beneficiario · Email · Valor · Dedicatoria.
 * Una fila con un solo campo = tarjeta anónima de ese valor.
 */
function parseLista(texto) {
  const filas = []
  String(texto || '').split(/\r?\n/).forEach((linea) => {
    const raw = linea.trim()
    if (!raw) return
    const cols = raw.split(/\t|;/).map(c => c.trim())
    let beneficiario, email, montoStr, dedicatoria
    if (cols.length === 1) {
      montoStr = cols[0]
    } else {
      beneficiario = cols[0]
      email = cols[1]
      montoStr = cols[2]
      dedicatoria = cols[3]
    }
    const monto = parseMonto(montoStr)
    filas.push({
      beneficiario_nombre: beneficiario || undefined,
      beneficiario_email: email || undefined,
      dedicatoria: dedicatoria || undefined,
      monto,
      _valido: Number.isFinite(monto) && monto > 0,
    })
  })
  return filas
}

/**
 * Emisión de gift cards SIN NV (cortesía / regalo institucional, con motivo).
 * Dos modos: individual (una tarjeta) y por lote (herramienta de venta B2B:
 * N tarjetas iguales, o una lista pegada con distintos valores/beneficiarios).
 * La emisión por venta es automática: NV con GIF2500 → al facturarse se emiten solas.
 */
export default function GiftCardFormPage() {
  const navigate = useNavigate()
  const [modo, setModo] = useState('individual') // 'individual' | 'lote'
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)
  const [resultado, setResultado] = useState(null) // { cantidad, total, cards }
  const [buscarCliente, setBuscarCliente] = useState(false)

  // Individual
  const [form, setForm] = useState({
    monto: '', vence_el: venceDefault(), cliente: null,
    beneficiario_nombre: '', beneficiario_email: '', de_nombre: '', dedicatoria: '', motivo: '',
    proposito: 'regalo', diseno: 'generica', enviar_el: '',
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Lote (común + submodo)
  const [lote, setLote] = useState({
    submodo: 'rapido', // 'rapido' | 'lista'
    cantidad: '', valor: '', pegado: '',
    vence_el: venceDefault(), cliente: null, motivo: '',
  })
  const setL = (k, v) => setLote(l => ({ ...l, [k]: v }))

  const filasLista = useMemo(() => parseLista(lote.pegado), [lote.pegado])
  const filasValidas = useMemo(() => filasLista.filter(f => f._valido), [filasLista])

  // Tarjetas a emitir según el submodo del lote
  const tarjetasLote = useMemo(() => {
    if (lote.submodo === 'rapido') {
      const n = Math.max(0, parseInt(lote.cantidad, 10) || 0)
      const monto = parseFloat(lote.valor) || 0
      return Array.from({ length: n }, () => ({ monto }))
    }
    return filasValidas.map(f => ({
      monto: f.monto,
      beneficiario_nombre: f.beneficiario_nombre,
      beneficiario_email: f.beneficiario_email,
      dedicatoria: f.dedicatoria,
    }))
  }, [lote.submodo, lote.cantidad, lote.valor, filasValidas])

  const totalLote = tarjetasLote.reduce((s, t) => s + (Number(t.monto) || 0), 0)

  const guardarIndividual = async () => {
    setError(null); setGuardando(true)
    try {
      const { data } = await giftCardsApi.crear({
        monto: parseFloat(form.monto) || 0,
        vence_el: form.vence_el || undefined,
        cliente_id: form.cliente?.id || undefined,
        beneficiario_nombre: form.beneficiario_nombre || undefined,
        beneficiario_email: form.beneficiario_email || undefined,
        de_nombre: form.de_nombre || undefined,
        dedicatoria: form.dedicatoria || undefined,
        motivo: form.motivo,
        proposito: form.proposito,
        diseno: form.diseno,
        enviar_el: form.enviar_el || undefined,
      })
      navigate(`/gift-cards/${data.id}`)
    } catch (e) {
      const errs = e.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || 'No se pudo emitir.'))
    } finally {
      setGuardando(false)
    }
  }

  const guardarLote = async () => {
    setError(null); setGuardando(true)
    try {
      const { data } = await giftCardsApi.crearLote({
        motivo: lote.motivo,
        vence_el: lote.vence_el || undefined,
        cliente_id: lote.cliente?.id || undefined,
        tarjetas: tarjetasLote,
      })
      setResultado(data)
    } catch (e) {
      const errs = e.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || 'No se pudo emitir el lote.'))
    } finally {
      setGuardando(false)
    }
  }

  const descargarLote = () => {
    const ids = (resultado?.cards || []).map(c => c.id)
    if (ids.length) descargarArchivo(giftCardsApi.pdfLoteUrl(ids), `gift_cards_lote_${ids.length}.pdf`)
  }

  // ── Pantalla de resultado del lote ────────────────────────────────────
  if (resultado) {
    return (
      <div style={{ maxWidth: 640 }}>
        <h1 style={{ margin: '0 0 16px' }}>Lote emitido</h1>
        <div style={{ ...sx.card, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: text.sec, color: colors.text, background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: 6 }}>
            ✅ Se emitieron <strong>{resultado.cantidad}</strong> gift cards por un total de <strong>${fmt(resultado.total)}</strong>.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Boton variante="primary" onClick={descargarLote}>⬇ Descargar todas (PDF)</Boton>
            <Boton variante="secondary" onClick={() => navigate('/gift-cards')}>Ver listado</Boton>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.sec }}>
              <thead>
                <tr style={{ background: colors.bgSoft, textAlign: 'left' }}>
                  <th style={{ padding: '6px 10px' }}>Código</th>
                  <th style={{ padding: '6px 10px' }}>Beneficiario</th>
                  <th style={{ padding: '6px 10px', textAlign: 'right' }}>Valor</th>
                </tr>
              </thead>
              <tbody>
                {resultado.cards.map(c => (
                  <tr key={c.id} style={{ borderTop: '1px solid #eef2f7', cursor: 'pointer' }} onClick={() => navigate(`/gift-cards/${c.id}`)}>
                    <td style={{ padding: '6px 10px', fontFamily: 'ui-monospace, monospace' }}>{c.codigo}</td>
                    <td style={{ padding: '6px 10px', color: c.beneficiario_nombre ? colors.text : colors.textMeta }}>{c.beneficiario_nombre || '—'}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>${fmt(c.monto_inicial)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  // ── Formulario ────────────────────────────────────────────────────────
  const clienteRow = (value, onQuitar) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <div style={{ flex: 1, fontSize: text.body, color: value ? colors.text : colors.textMeta }}>
        {value ? value.nombre : 'Sin cliente vinculado'}
      </div>
      <Boton variante="secondary" sm onClick={() => setBuscarCliente(true)}>🔍 Buscar</Boton>
      {value && <Boton variante="ghost" sm onClick={onQuitar}>✕</Boton>}
    </div>
  )

  const tab = (id, label) => (
    <button
      onClick={() => { setModo(id); setError(null) }}
      style={{
        flex: 1, padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: text.sec,
        border: `1px solid ${modo === id ? (colors.primary || '#1e40af') : colors.textPlh}`,
        background: modo === id ? (colors.primary || '#1e40af') : '#fff',
        color: modo === id ? '#fff' : colors.textSec,
      }}
    >{label}</button>
  )

  const subTab = (id, label) => (
    <button
      onClick={() => setL('submodo', id)}
      style={{
        flex: 1, padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: text.sec, fontWeight: 600,
        border: `1px solid ${lote.submodo === id ? (colors.primary || '#1e40af') : colors.textPlh}`,
        background: lote.submodo === id ? '#eff6ff' : '#fff', color: colors.textSec,
      }}
    >{label}</button>
  )

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Boton variante="ghost" sm onClick={() => navigate(-1)}>← Volver</Boton>
        <h1 style={{ margin: 0 }}>Emitir gift card</h1>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {tab('individual', 'Individual')}
        {tab('lote', 'Por lote')}
      </div>

      <div style={{ ...sx.card, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: text.sec, color: colors.textSec, background: colors.bgSoft, padding: '8px 12px', borderRadius: 6 }}>
          Emisión manual, para cortesías y regalos institucionales{modo === 'lote' ? ' — herramienta de venta para empresas' : ''}. Las gift cards <strong>vendidas</strong> se
          emiten solas al facturarse la NV con el artículo GIF2500.
        </div>

        {modo === 'individual' ? (
          <>
            <div style={{ display: 'flex', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={lblSx}>Valor *</label>
                <MoneyInput value={form.monto} onChange={v => set('monto', v)} placeholder="0,00" style={inputSx} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lblSx}>Vence el</label>
                <input type="date" value={form.vence_el} onChange={e => set('vence_el', e.target.value)} style={inputSx} />
              </div>
            </div>

            <div>
              <label style={lblSx}>Motivo *</label>
              <input value={form.motivo} onChange={e => set('motivo', e.target.value)}
                placeholder="Ej: cortesía evento Casa VG, compensación pedido 91XXXX…" style={inputSx} />
            </div>

            {/* Propósito: define el COPY del mail que recibe el destinatario
                (regalo = "te regalaron…", descuento = "te regalamos $X…",
                fidelización = agradecimiento, otro = genérico). */}
            <div>
              <label style={lblSx}>¿Para qué es? (define el mail que recibe)</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[['regalo', '🎁 Regalo'], ['descuento', '💸 Descuento'], ['fidelizacion', '🤝 Fidelización'], ['otro', 'Otro']].map(([v, txt]) => (
                  <button key={v} type="button" onClick={() => set('proposito', v)}
                    style={{
                      padding: '7px 14px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
                      border: form.proposito === v ? '1px solid #1e40af' : '1px solid #e2e8f0',
                      background: form.proposito === v ? '#eff6ff' : '#fff',
                      color: form.proposito === v ? '#1e40af' : '#475569',
                    }}>{txt}</button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={lblSx}>Diseño de la tarjeta</label>
                <select value={form.diseno} onChange={e => set('diseno', e.target.value)} style={inputSx}>
                  <option value="generica">Genérica — verde ("Gift Card")</option>
                  <option value="cumpleanos">Cumpleaños — negro ("Feliz cumpleaños")</option>
                  <option value="enamorados">Enamorados — bordó ("Brindemos por lo nuestro")</option>
                  <option value="fiestas">Fiestas — marfil ("Felices Fiestas")</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lblSx}>Mandar el mail el… (opcional)</label>
                <input type="date" value={form.enviar_el} onChange={e => set('enviar_el', e.target.value)} style={inputSx} />
              </div>
            </div>

            <div>
              <label style={lblSx}>Cliente (opcional)</label>
              {clienteRow(form.cliente, () => set('cliente', null))}
            </div>

            <div style={{ display: 'flex', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={lblSx}>Beneficiario</label>
                <input value={form.beneficiario_nombre} onChange={e => set('beneficiario_nombre', e.target.value)}
                  placeholder="Quién la recibe" style={inputSx} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lblSx}>Email del beneficiario</label>
                <input type="email" value={form.beneficiario_email} onChange={e => set('beneficiario_email', e.target.value)}
                  placeholder="si lo cargás, le llega el mail con la tarjeta" style={inputSx} />
              </div>
            </div>

            <div>
              <label style={lblSx}>De parte de (firma el mensaje del mail)</label>
              <input value={form.de_nombre} onChange={e => set('de_nombre', e.target.value)}
                placeholder="Opcional — ej: Martin, Vinoteca Ligier" style={inputSx} />
            </div>

            <div>
              <label style={lblSx}>Dedicatoria (sale en el PDF)</label>
              <textarea value={form.dedicatoria} onChange={e => set('dedicatoria', e.target.value)} rows={3}
                placeholder="Opcional" style={{ ...inputSx, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              {subTab('rapido', 'Cantidad × mismo valor')}
              {subTab('lista', 'Lista pegada (distintos)')}
            </div>

            {lote.submodo === 'rapido' ? (
              <div style={{ display: 'flex', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={lblSx}>Cantidad *</label>
                  <input type="number" min="1" value={lote.cantidad} onChange={e => setL('cantidad', e.target.value)}
                    placeholder="Ej: 50" style={inputSx} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lblSx}>Valor c/u *</label>
                  <MoneyInput value={lote.valor} onChange={v => setL('valor', v)} placeholder="0,00" style={inputSx} />
                </div>
              </div>
            ) : (
              <div>
                <label style={lblSx}>Lista (pegar desde Excel)</label>
                <div style={{ fontSize: text.meta, color: colors.textMeta, marginBottom: 6 }}>
                  Una fila por tarjeta. Columnas: <strong>Beneficiario · Email · Valor · Dedicatoria</strong> (separadas por tab o «;»). El valor es obligatorio.
                </div>
                <textarea value={lote.pegado} onChange={e => setL('pegado', e.target.value)} rows={7}
                  placeholder={'Juan Pérez\tjuan@acme.com\t20000\nAna López\tana@acme.com\t15000'}
                  style={{ ...inputSx, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: text.sec }} />
                {filasLista.length > 0 && (
                  <div style={{ fontSize: text.sec, marginTop: 6, color: colors.textSec }}>
                    {filasValidas.length} tarjeta(s) válida(s)
                    {filasLista.length !== filasValidas.length && (
                      <span style={{ color: colors.err }}> · {filasLista.length - filasValidas.length} fila(s) sin valor válido (se ignoran)</span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={lblSx}>Vence el</label>
                <input type="date" value={lote.vence_el} onChange={e => setL('vence_el', e.target.value)} style={inputSx} />
              </div>
              <div style={{ flex: 1 }} />
            </div>

            <div>
              <label style={lblSx}>Motivo *</label>
              <input value={lote.motivo} onChange={e => setL('motivo', e.target.value)}
                placeholder="Ej: regalo corporativo ACME 2026" style={inputSx} />
            </div>

            <div>
              <label style={lblSx}>Empresa / cliente (opcional)</label>
              {clienteRow(lote.cliente, () => setL('cliente', null))}
            </div>

            <div style={{ fontSize: text.body, background: colors.bgSoft, padding: '10px 14px', borderRadius: 6, display: 'flex', justifyContent: 'space-between' }}>
              <span>{tarjetasLote.length} tarjeta(s)</span>
              <span>Total: <strong>${fmt(totalLote)}</strong></span>
            </div>
          </>
        )}

        {error && (
          <div style={{ background: colors.errBg, color: colors.err, padding: '8px 12px', borderRadius: 6, fontSize: text.sec }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Boton variante="ghost" onClick={() => navigate(-1)}>Cancelar</Boton>
          {modo === 'individual' ? (
            <Boton variante="primary" onClick={guardarIndividual} disabled={guardando || !form.monto || !form.motivo.trim()}>
              {guardando ? 'Emitiendo…' : '🎁 Emitir'}
            </Boton>
          ) : (
            <Boton variante="primary" onClick={guardarLote} disabled={guardando || tarjetasLote.length === 0 || !lote.motivo.trim()}>
              {guardando ? 'Emitiendo…' : `🎁 Emitir ${tarjetasLote.length || ''} tarjeta(s)`}
            </Boton>
          )}
        </div>
      </div>

      {buscarCliente && (
        <ModalListaClientes
          onSelect={c => {
            if (modo === 'individual') {
              setForm(f => ({ ...f, cliente: c, beneficiario_nombre: f.beneficiario_nombre || c.nombre, beneficiario_email: f.beneficiario_email || c.email || '' }))
            } else {
              setL('cliente', c)
            }
            setBuscarCliente(false)
          }}
          onClose={() => setBuscarCliente(false)}
        />
      )}
    </div>
  )
}
